import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import * as Sentry from '@sentry/browser'
import axios from 'axios'

import { AllStartupProps, IStartupPropsFromState } from './types'

import { authDashlyUser, identifyDashlyUser, Mixpanel, trackDashlyEvent } from '@/analytics'
import Loader from '@/components/Loader'
import { getBrowserLanguage } from '@/locales/helpers'
import { setAppLoaded } from '@/store/app'
import { getReferral } from '@/store/referral'
import { MyThunkDispatch } from '@/store/store'
import {
  getProfile,
  getUserAvailablePlugins,
  getUserInfo,
  getUserPlugins,
  logoutUser,
  refreshToken,
  setAuthorized,
} from '@/store/user'
import { CloseDocType, IUserInfoResponse } from '@/store/user/types'
import { DashlyQuestEvents, DashlyQuestProps } from '@/types'
import IApplicationState from '@/types/state'
import { baseCatchError, jwt } from '@/utils'

export class Startup extends PureComponent<AllStartupProps> {
  constructor(props: AllStartupProps) {
    super(props)

    this.setLoadedWithoutAuth = this.setLoadedWithoutAuth.bind(this)
  }

  public async componentDidMount() {
    try {
      const tokens = jwt.get()
      window.localStorage.setItem('activeFilter', '')
      Mixpanel.track('VisitTool')

      if (!tokens.accessToken && !tokens.refToken) {
        return this.setLoadedWithoutAuth()
      }

      const userResponse = await this.props.getUserInfo()
      if (userResponse.status !== 200) {
        return this.props.logout()
      }

      const user = userResponse.data
      await this.setupUserAnalytics(user)
      await this.loadUserProfile(user)

      this.setupDashly(user)
    } catch (error) {
      this.setLoadedWithoutAuth()
    }
  }

  private async setupUserAnalytics(user: IUserInfoResponse) {
    Sentry.configureScope((scope) => {
      scope.setUser({ email: user.email })
    })

    Mixpanel.set({
      $email: user.email.toLowerCase(),
      $name: user.first_name,
      Language: user.preferred_language,
      Country: user.country.name,
    })
    Mixpanel.identify(user.email)
  }

  private async loadUserProfile(user: IUserInfoResponse) {
    try {
      const profileResponse = await this.props.getProfile()

      if (profileResponse.status !== 200) {
        return
      }

      const profile = profileResponse.data

      Mixpanel.set({
        'Archived AdAccounts': profile.account_counts.archived_accounts,
        'Client ID': profile.id,
        'Facebook AdAccounts': profile.account_counts.facebook,
        Industry: profile.industry,
        'Paying Customer': profile.paying_customer ? 'Yes' : 'No',
        'TikTok AdAccounts': profile.account_counts.tiktok,
        ProfileType: profile.profile_type,
        PaymentMethod: profile.payment_method,
        CloseDocType: profile.closedoc_type,
      })

      if (user.has_profile) {
        await this.loadUserPlugins(user.email)
        this.props.getUserAvailablePlugins().catch(baseCatchError)

        if (profile.closedoc_type !== CloseDocType.spend) {
          this.props.getReferral().catch(baseCatchError)
        }
      } else {
        this.props.setLoaded(true)
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        baseCatchError(error)
      }
      this.props.setLoaded(true)
    }
  }

  private async loadUserPlugins(email: string) {
    try {
      await this.props.getUserPlugins()
      await this.props.setLoaded(true)

      Mixpanel.identify(email)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        baseCatchError(error)
      }
    }
  }

  private setupDashly(user: IUserInfoResponse) {
    const winw = window
    const authKey = process.env.REACT_APP_DASHLY_AUTH_KEY

    if (!winw.dashly) {
      return
    }

    authDashlyUser({ userId: user.id })

    if (user.id && authKey) {
      trackDashlyEvent(DashlyQuestEvents.AUTH, {
        $email: user.email.toLowerCase(),
        $name: user.first_name,
      })
    }

    const browserLanguage = getBrowserLanguage()
    identifyDashlyUser({
      [DashlyQuestProps.DEVICE_LANGUAGE]: browserLanguage,
    })
  }

  public render() {
    return this.props.loaded ? this.props.children : <Loader />
  }

  public setLoadedWithoutAuth() {
    this.props.setAuthorized(false)
    this.props.setLoaded(true)
  }
}

const mapStateToProps = (state: IApplicationState) => ({
  authorized: state.user.isAuthorized,
  email: state.user.email,
  loaded: state.app.loaded,
  user: state.user,
})

const mapDispatchToProps = (dispatch: MyThunkDispatch) => ({
  getProfile: () => dispatch(getProfile()),
  getReferral: () => dispatch(getReferral()),
  getUserAvailablePlugins: () => dispatch(getUserAvailablePlugins()),
  getUserInfo: () => dispatch(getUserInfo()),
  getUserPlugins: () => dispatch(getUserPlugins()),
  logout: () => dispatch(logoutUser()),
  refreshToken: (token: string | null) => dispatch(refreshToken(token)),
  setAuthorized: (authorized: boolean) => dispatch(setAuthorized(authorized)),
  setLoaded: (loaded: boolean) => dispatch(setAppLoaded(loaded)),
})

const StartupClass = connect<IStartupPropsFromState, any, any, any>(mapStateToProps, mapDispatchToProps)(Startup)

export default StartupClass
